import React, { useMemo, useState } from "react";
import {
  Alert, Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, Stack, TextField, Typography,
} from "@mui/material";
import WebhookRoundedIcon from "@mui/icons-material/WebhookRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import {
  useGetWebhooksQuery, useCreateWebhookMutation, useDeleteWebhookMutation,
} from "../../../Api/platformApi";
import {
  AppPage, PageHeader, FilterBar, SearchBox, DataTable, StatusBadge,
  ActionButtons, ActionButton, ConfirmDialog, CardSurface, type DataTableColumn,
} from "../../../Components/ui";
import { NAVY } from "../../../theme/tokens";

const EVENTS = ["message.received", "message.delivered", "message.failed", "conversation.created"];

const Webhooks: React.FC = () => {
  const { data, isLoading } = useGetWebhooksQuery();
  const [createWebhook] = useCreateWebhookMutation();
  const [deleteWebhook] = useDeleteWebhookMutation();

  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState("");
  const [events, setEvents] = useState<string[]>(["message.received"]);
  const [secret, setSecret] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [confirmId, setConfirmId] = useState<number | null>(null);

  const rows = data?.data ?? [];

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((w: any) =>
      [w.url, ...(w.events ?? [])].filter(Boolean).some((v: string) => v.toLowerCase().includes(q)));
  }, [rows, search]);

  const toggle = (e: string) =>
    setEvents((s) => s.includes(e) ? s.filter((x) => x !== e) : [...s, e]);

  const save = async () => {
    const res: any = await createWebhook({ url, events }).unwrap();
    setSecret(res.secret);
  };

  const openCreate = () => {
    setUrl("");
    setEvents(["message.received"]);
    setSecret(null);
    setOpen(true);
  };

  const columns: DataTableColumn<any>[] = [
    {
      id: "url", label: "URL",
      render: (w) => (
        <Typography fontSize={13} sx={{ maxWidth: 280, wordBreak: "break-all" }}>{w.url}</Typography>
      ),
    },
    {
      id: "events", label: "Events",
      render: (w) => (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
          {(w.events ?? []).map((e: string) => (
            <StatusBadge key={e} status={e} tone="info" label={e} />
          ))}
        </Box>
      ),
    },
    {
      id: "active", label: "Active",
      render: (w) => <StatusBadge status={w.active ? "active" : "inactive"} />,
    },
    { id: "last_called_at", label: "Last Called", render: (w) => w.last_called_at ?? "never" },
  ];

  return (
    <AppPage
      disablePaper
      header={
        <PageHeader
          icon={<WebhookRoundedIcon />}
          title="Webhooks"
          subtitle="HTTP callbacks for platform events"
          actions={
            <ActionButton startIcon={<AddRoundedIcon />} onClick={openCreate}>
              Add Webhook
            </ActionButton>
          }
        />
      }
    >
      <Alert severity="info" sx={{ mb: 2 }}>
        INTALK calls your URL on events, signed with HMAC-SHA256 in the
        <Box component="code" sx={{ mx: 0.5 }}>X-INTALK-SIGNATURE</Box>
        header. Failed deliveries retry at 5 min, 30 min, 2 hours.
      </Alert>

      <FilterBar right={<Typography fontSize={13} color="text.secondary">{filtered.length} webhooks</Typography>}>
        <SearchBox value={search} onChange={setSearch} placeholder="Search webhooks…" />
      </FilterBar>

      <DataTable
        columns={columns}
        rows={filtered}
        loading={isLoading}
        actions={(w) => (
          <ActionButtons
            onDelete={() => setConfirmId(w.id)}
          />
        )}
        empty={{
          icon: <WebhookRoundedIcon />,
          title: "No webhooks yet",
          message: "Add a webhook endpoint to receive real-time event notifications.",
          action: <ActionButton startIcon={<AddRoundedIcon />} onClick={openCreate}>Add Webhook</ActionButton>,
        }}
      />

      {/* Add webhook / reveal-once secret dialog — kept as Dialog to preserve the reveal-once secret flow */}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: "16px" } }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: NAVY }}>
          {secret ? "Webhook Created" : "Add Webhook"}
        </DialogTitle>
        <DialogContent>
          {!secret ? (
            <Stack spacing={2} mt={1}>
              <TextField
                label="Callback URL"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                fullWidth
                required
                placeholder="https://dcc-crm.example.com/intalk/webhook"
              />
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Events</Typography>
                {EVENTS.map((e) => (
                  <FormControlLabel
                    key={e}
                    control={<Checkbox checked={events.includes(e)} onChange={() => toggle(e)} />}
                    label={e}
                  />
                ))}
              </Box>
            </Stack>
          ) : (
            <CardSurface noHover sx={{ p: 2, mt: 1 }}>
              <Alert severity="success" sx={{ mb: 1.5, borderRadius: "10px" }}>
                Signing secret (store it now — shown once):
              </Alert>
              <Box
                component="code"
                sx={{ display: "block", wordBreak: "break-all", fontSize: 13, color: NAVY, userSelect: "all" }}
              >
                {secret}
              </Box>
            </CardSurface>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button variant="outlined" onClick={() => setOpen(false)} sx={{ borderRadius: "10px" }}>
            {secret ? "Done" : "Cancel"}
          </Button>
          {!secret && (
            <ActionButton onClick={save} disabled={!url || events.length === 0}>Create</ActionButton>
          )}
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={confirmId !== null}
        title="Delete webhook?"
        message="This removes the endpoint. No further events will be delivered to it."
        confirmText="Delete"
        danger
        onClose={() => setConfirmId(null)}
        onConfirm={async () => { if (confirmId !== null) await deleteWebhook(confirmId); setConfirmId(null); }}
      />
    </AppPage>
  );
};

export default Webhooks;
